import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useParams, useNavigate } from 'react-router-dom';
import Header from './components/Header';
import Hero from './components/Hero';
import TabbedContent from './components/TabbedContent';
import Footer from './components/Footer';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import Impressum from './pages/Impressum';
import ServiceDetail from './pages/ServiceDetail';
import MeetTheTeam from './pages/MeetTheTeam';
import MobileTabBar from './components/MobileTabBar';
import ChatWidget from './components/ChatWidget';
import { LanguageProvider } from './i18n';
import { ThemeProvider } from './theme';
import { ViewProvider, useViewContext } from './viewContext';
import { initGA, trackPageView } from './analytics';

const Home = () => {
  const location = useLocation();
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState(slug ? 'projects' : (location.state?.tab || 'introduction'));
  const { setView } = useViewContext();

  // Report the active homepage section to Dom.
  useEffect(() => {
    const names = {
      introduction: 'the Introduction / About section on the homepage',
      capabilities: 'the Services section on the homepage',
      expertise: 'the Expertise section on the homepage',
      projects: 'the Projects section on the homepage (project gallery)',
      contact: 'the Contact section on the homepage',
    };
    setView(`The visitor is browsing ${names[activeTab] || 'the homepage'}.`);
    trackPageView(`/?section=${activeTab}`);
  }, [activeTab, setView]);

  const goToTab = (id) => {
    setActiveTab(id);
  };

  const goToTabScroll = (id) => {
    setActiveTab(id);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="tabbed-content"]');
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 60);
  };

  // Deep-link entry (/projects/:slug): activate the Projects tab and scroll it into view.
  useEffect(() => {
    if (slug) {
      setActiveTab('projects');
      setTimeout(() => {
        const el = document.querySelector('[data-testid="tabbed-content"]');
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 200);
    }
  }, [slug]);

  // When navigated back from a sub-page with a target tab, activate it and scroll into view.
  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="tabbed-content"]');
        if (el) {
          const top = el.getBoundingClientRect().top + window.scrollY - 90;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 120);
    }
  }, [location.state]);

  // Keyboard navigation: ←/→ switch tabs, Enter opens the current tab's primary item.
  useEffect(() => {
    const order = ['introduction', 'capabilities', 'expertise', 'projects', 'contact'];
    const onKey = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag) || e.target.isContentEditable) return;
      if (document.querySelector('[data-testid="project-modal"]')) return; // modal handles its own keys
      const idx = order.indexOf(activeTab);
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const next = e.key === 'ArrowRight' ? (idx + 1) % order.length : (idx - 1 + order.length) % order.length;
        goToTabScroll(order[next]);
      } else if (e.key === 'Enter') {
        const sel = {
          projects: '[data-testid="project-spotlight"]',
          capabilities: '[data-testid="service-card-01"]',
          contact: '[data-testid="input-name"]'
        }[activeTab];
        const el = sel && document.querySelector(sel);
        if (el) { e.preventDefault(); el.focus?.(); if (activeTab !== 'contact') el.click?.(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab]);

  return (
    <div className="bg-navy">
      <Header activeTab={activeTab} onTabChange={goToTabScroll} onLogoClick={() => { setActiveTab('introduction'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
      <main>
        <Hero
          onQuote={() => goToTabScroll('contact')}
          onExplore={() => goToTabScroll('capabilities')}
        />
        <TabbedContent activeTab={activeTab} onTabChange={goToTab} deepProjectSlug={slug} />
      </main>
      <Footer onTabChange={goToTabScroll} />
      {/* Native-app style bottom navigation (mobile only) */}
      <MobileTabBar activeTab={activeTab} onTabChange={goToTab} />
      <div className="h-24 lg:hidden" aria-hidden="true" />
    </div>
  );
};

// Fires GA page_view on route changes (and inits GA once).
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => { initGA(); }, []);
  useEffect(() => { trackPageView(location.pathname + location.search); }, [location.pathname, location.search]);
  return null;
};

// Dom AI assistant, rendered globally so it appears on every page.
const GlobalChat = () => {
  const navigate = useNavigate();
  const { view } = useViewContext();
  return <ChatWidget onQuote={() => navigate('/', { state: { tab: 'contact' } })} viewContext={view} />;
};

function App() {
  useEffect(() => {
    const blockOnImage = (e) => {
      if (e.target && e.target.tagName === 'IMG') e.preventDefault();
    };
    document.addEventListener('contextmenu', blockOnImage);
    document.addEventListener('dragstart', blockOnImage);
    return () => {
      document.removeEventListener('contextmenu', blockOnImage);
      document.removeEventListener('dragstart', blockOnImage);
    };
  }, []);

  return (
    <div className="App bg-navy">
      <ThemeProvider>
        <LanguageProvider>
          <ViewProvider>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/projects/:slug" element={<Home />} />
                <Route path="/services/:slug" element={<ServiceDetail />} />
                <Route path="/meet-the-team" element={<MeetTheTeam />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-and-conditions" element={<TermsConditions />} />
                <Route path="/impressum" element={<Impressum />} />
              </Routes>
              <RouteTracker />
              <GlobalChat />
            </BrowserRouter>
          </ViewProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
